import { useMemo, type ReactNode } from 'react';
import { EquityCurve, OutcomeHistogram, Streaks, ReachCurve, type ChartTrade } from '../components/ledger/Charts';
import { INK, ICE, TEAL, TITLE_FONT, PX_FONT, pixelFrame, arcadeTitleShadow } from '../components/guild/theme';

/* Deterministic sample book: ~55% of trades reach 1R, max run decays like the real board */
function sampleTrades(n = 80): ChartTrade[] {
  let seed = 7;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  return Array.from({ length: n }, () => {
    const mr = rand() < 0.3 ? rand() * 0.9 : -Math.log(1 - rand()) * 1.8;
    const r = mr < 1 ? -1 : Math.max(-1, Math.round((mr * (0.45 + rand() * 0.5)) * 10) / 10);
    return { mr: Math.round(mr * 10) / 10, r };
  });
}

function Card({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section style={{ background: 'rgba(7,32,42,0.92)', padding: 20, boxShadow: pixelFrame() }}>
      <div style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE, textShadow: `0 2px 0 ${INK}` }}>{title}</div>
      <div style={{ fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.1em', color: TEAL, margin: '4px 0 14px' }}>{hint}</div>
      {children}
    </section>
  );
}

export function LedgerChartsPreview() {
  const trades = useMemo(() => sampleTrades(), []);
  return (
    <div style={{ minHeight: '100vh', background: '#051A22', padding: '32px 24px 64px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <h1 style={{ margin: 0, fontFamily: TITLE_FONT, fontSize: 36, letterSpacing: '0.1em', color: ICE, textShadow: arcadeTitleShadow }}>LEDGER CHARTS</h1>
        <div style={{ fontFamily: PX_FONT, fontSize: 11, letterSpacing: '0.12em', color: TEAL }}>Sample data — 80 made-up trades, just to see the visuals</div>
        <Card title="Reach curve" hint="Share of trades that ran at least this far. Each marker is an exit level: how often price reached it at all."><ReachCurve trades={trades} /></Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 24 }}>
          <Card title="Equity curve" hint="Cumulative R, trade by trade. Red = drawdown from the peak."><EquityCurve trades={trades} /></Card>
          <Card title="Outcomes" hint="How many trades landed in each half-R bucket."><OutcomeHistogram trades={trades} /></Card>
          <Card title="Streaks" hint="Your recent run of wins and losses."><Streaks trades={trades} /></Card>
          
        </div>
      </div>
    </div>
  );
}
